import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { ReactComponent as InfoCircle } from 'assets/images/InfoCircle.svg?react';
import RootInput from 'components/Input/RootInput/RootInput';
import { type InputName } from 'components/Input/templates/Location/LocationInput';
import getAddressParts from 'components/Input/templates/Location/utils';
import useAccessibleDropdown from 'components/Select/hook';
import { type TooltipMessage } from 'enums/Messages';
import {
  type FormattedSuggestion,
  AUTOCOMPLETE_STATUS,
  useGoogleAutocomplete,
} from 'hooks/useGoogleAutocomplete';
import { isDebounceCancelledError } from 'utils/debounce';
import { leaveOnlyNumbers } from 'utils/leaveOnlyNumbers';
import { logger } from 'utils/logger';
import {
  ADDRESS_FIELDS,
  GOOGLE_MAPS_DEBOUNCE_DELAY,
  GOOGLE_MAPS_PRIMARY_TYPES,
  MAX_STREET_INPUT_LENGTH,
  MIN_INPUT_LENGTH_FOR_SEARCH,
} from './config';
import Suggestions from './Suggestions';
import styles from './StreetInput.module.scss';

interface Props {
  names: InputName;
  label: InputName['street'];
  googlePlacesScriptLoaded: boolean;
  containerClassName?: string;
  required: boolean;
  streetTooltip: TooltipMessage;
}

const StreetInput = (props: Props) => {
  const { names, label, googlePlacesScriptLoaded, containerClassName, required, streetTooltip } =
    props;
  const autocompleteId = useMemo(uuidv4, []);

  const [streetInputFocused, setStreetInputFocused] = useState<boolean>(false);
  const optionsRef = useRef<HTMLUListElement>(null);
  const { control, setError, clearErrors, setValue, setFocus } = useFormContext();
  const suggestionsListId = `${names.street}-select-list`;

  const { suggestions, status, fetchSuggestions, clearSuggestions, refreshSessionToken } =
    useGoogleAutocomplete({
      isScriptLoaded: googlePlacesScriptLoaded,
      debounceDelay: GOOGLE_MAPS_DEBOUNCE_DELAY,
    });

  const handleOnChange = (value: string) => {
    if (!value || value.length < MIN_INPUT_LENGTH_FOR_SEARCH) {
      clearSuggestions();

      return;
    }

    fetchSuggestions(value, {
      types: GOOGLE_MAPS_PRIMARY_TYPES,
    }).catch((error: unknown) => {
      if (!isDebounceCancelledError(error)) {
        logger.error('Google Maps Autocomplete Error:', error);
      }
    });
  };

  const handleState = useCallback(
    (name: string, value?: string) => {
      if (value) {
        setValue(name, value, { shouldValidate: true });
        clearErrors(name);
      }
    },
    [setValue, clearErrors],
  );

  const chooseAddress = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      const { placePrediction } = suggestion;

      if (!placePrediction) {
        return;
      }

      try {
        const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
        const place = new Place({ id: placePrediction.placeId });

        await place.fetchFields({
          fields: ADDRESS_FIELDS,
        });

        const streetName =
          placePrediction.text?.toString().split(',')[0] ||
          place.formattedAddress?.split(',')[0] ||
          '';

        handleState(names.street, streetName);

        const components = place.addressComponents;

        if (components) {
          const addressParts = getAddressParts(components);

          handleState(names.city, addressParts.city);
          handleState(names.state, addressParts.state);
          handleState(names.zip, leaveOnlyNumbers(addressParts.zip));
        }

        clearSuggestions();
        refreshSessionToken();
      } catch (error) {
        logger.error('Error fetching place details:', error);
      }
    },
    [names.city, names.state, names.street, names.zip],
  );

  const { dropdownOpened, toggleDropdown, optionsActive } =
    useAccessibleDropdown<FormattedSuggestion>({
      options: suggestions,
      onChange: (item) => chooseAddress(item.original),
      optionsRef,
      isMainInputFocused: streetInputFocused,
      isMainInputTypingEnabled: true,
      setMainInputFocus: () => {
        setFocus(names.street, {});
      },
    });

  useEffect(() => {
    toggleDropdown(status === AUTOCOMPLETE_STATUS.OK);
  }, [status, toggleDropdown]);

  const outsideClickRef = useOnclickOutside(clearSuggestions);

  return (
    <div className={clsx(styles.suggestion, containerClassName)} ref={outsideClickRef}>
      <RootInput
        inputClassName={styles.streetInput}
        maxLength={MAX_STREET_INPUT_LENGTH}
        name={names.street}
        control={control}
        label={label}
        externalOnChange={handleOnChange}
        externalOnFocus={() => setStreetInputFocused(true)}
        externalOnBlur={() => setStreetInputFocused(false)}
        tooltipText={streetTooltip}
        icon={<InfoCircle role="presentation" />}
        setError={setError}
        clearErrors={clearErrors}
        onChangeResetTooltip
        handleIconClick={clearSuggestions}
        autoComplete={autocompleteId}
        required={required}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpened}
        aria-autocomplete="list"
        aria-controls={dropdownOpened ? suggestionsListId : undefined}
        aria-owns={dropdownOpened ? suggestionsListId : undefined}
      />
      <Suggestions
        ref={optionsRef}
        data={suggestions}
        optionsActive={optionsActive}
        handleClick={chooseAddress}
        suggestionsListId={suggestionsListId}
        isOpened={dropdownOpened}
      />
    </div>
  );
};

export default memo(StreetInput);
